import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { SearchInput } from "@/components/SearchInput";
import { StepperTimeline } from "@/components/StepperTimeline";
import { DataPanel } from "@/components/DataPanel";
import { ShareLink } from "@/components/ShareLink";
import { TrackingMap } from "@/components/TrackingMap";
import { trackPortal } from "@/lib/api";
import { formatDateTime, formatWindow } from "@/lib/format";
import {
  ArrowLeft,
  PackageSearch,
  RefreshCw,
  CheckCircle2,
  Clock,
  Truck,
  AlertTriangle,
  Loader2,
  FileText,
  ExternalLink,
} from "lucide-react";

const STATUS_STYLE = {
  DELIVERED: { bg: "bg-emerald-100", text: "text-emerald-700", dot: "bg-emerald-500", icon: CheckCircle2 },
  ON_A_WAY_TO_CONSIGNEE: { bg: "bg-indigo-100", text: "text-indigo-700", dot: "bg-indigo-500", icon: Truck },
  UNLOADED: { bg: "bg-indigo-100", text: "text-indigo-700", dot: "bg-indigo-500", icon: Truck },
  DISPATCHED: { bg: "bg-indigo-100", text: "text-indigo-700", dot: "bg-indigo-500", icon: Truck },
  PICKED_UP: { bg: "bg-indigo-100", text: "text-indigo-700", dot: "bg-indigo-500", icon: Truck },
  DISPOSITION: { bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500", icon: Clock },
  ACCEPTED: { bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500", icon: Clock },
};

const TRANSPORT_LABELS = {
  GROUPAGE: "Stückgut (Sammelgut)",
  DIRECT: "Direktfahrt",
  PARTLOAD: "Teilladung",
  FULL_TRUCK_LOAD: "Komplettladung",
};

export const TrackingPage = () => {
  const { reference } = useParams();
  const [searchParams] = useSearchParams();
  const plz = searchParams.get("plz") || "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!reference || !plz) {
      setLoading(false);
      setError("Sendungsnummer und PLZ erforderlich.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await trackPortal(reference, plz);
      setData(res);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let msg;
      if (typeof detail === "string" && detail.length > 0 && detail.length < 300) {
        msg = detail;
      } else if (status === 404 || status === 400) {
        msg = "Sendung nicht gefunden. Bitte Sendungsnummer und PLZ des Empfängers prüfen.";
      } else if (e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "")) {
        msg = "Die Anfrage hat zu lange gedauert. Bitte in einem Moment erneut versuchen.";
      } else {
        msg = "Ein Fehler ist aufgetreten. Bitte Eingabe prüfen und in einem Moment erneut versuchen.";
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [reference, plz]);

  useEffect(() => {
    load();
  }, [load]);

  const currentType = data?.steps?.slice().reverse().find((s) => s.status === "SUCCESS")?.type;
  const currentStep = data?.steps?.find((s) => s.type === currentType);
  const currentEvent = data?.currentStatus;
  const found = data?.found;
  const order = data?.order || {};
  const style = STATUS_STYLE[currentType] || {
    bg: "bg-slate-100", text: "text-slate-700", dot: "bg-slate-400", icon: Clock,
  };
  const StatusIcon = style.icon;

  const pickLatest = (key) => {
    if (!data?.events?.length) return null;
    for (let i = data.events.length - 1; i >= 0; i--) {
      if (data.events[i][key]) return data.events[i][key];
    }
    return null;
  };
  const collection = formatWindow(
    pickLatest("estimatedCollectionAtFrom") || currentEvent?.estimatedCollectionAtFrom,
    pickLatest("estimatedCollectionAtUntil") || currentEvent?.estimatedCollectionAtUntil,
  );
  const delivery = formatWindow(
    pickLatest("estimatedDeliveryAtFrom") ||
      pickLatest("estimatedArrivalAtFrom") ||
      currentEvent?.estimatedDeliveryAtFrom,
    pickLatest("estimatedDeliveryAtUntil") ||
      pickLatest("estimatedArrivalAtUntil") ||
      currentEvent?.estimatedDeliveryAtUntil,
  );

  const proofEvent = data?.events?.find(
    (e) => e.code === "91" && e.message && e.message.length > 5,
  );
  const proofUrl = proofEvent
    ? proofEvent.message.startsWith("http")
      ? proofEvent.message
      : `https://${proofEvent.message}`
    : null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="tracking-page">
      <Header />

      {/* Sub-header bar */}
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-6 py-6 md:flex-row md:items-end md:justify-between md:px-10 md:py-7">
          <div className="min-w-0">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
              data-testid="back-link"
            >
              <ArrowLeft size={14} /> Zurück
            </Link>
            <div className="mt-2 text-xs font-medium uppercase tracking-wider text-slate-500">
              Sendungsnummer
            </div>
            <div
              className="font-mono truncate text-xl font-bold text-slate-900 md:text-2xl"
              data-testid="tracking-reference"
            >
              {reference}
            </div>
          </div>
          <div className="flex w-full max-w-xl items-center gap-3">
            <div className="flex-1">
              <SearchInput compact />
            </div>
            <button
              onClick={load}
              className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white p-3 text-slate-600 transition-colors hover:border-indigo-300 hover:text-indigo-600"
              title="Aktualisieren"
              data-testid="refresh-button"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-6 py-8 md:px-10 md:py-12">
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorBlock message={error} />
        ) : !found ? (
          <EmptyResult reference={reference} />
        ) : (
          <div className="space-y-6" data-testid="tracking-grid">
            {/* STATUS HERO CARD */}
            <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200 md:p-10">
              <div className="grid grid-cols-1 gap-8 md:grid-cols-12">
                <div className="md:col-span-7">
                  <div
                    className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${style.bg} ${style.text}`}
                    data-testid="tracking-status-badge"
                  >
                    <span className={`inline-block h-1.5 w-1.5 rounded-full ${style.dot} ${currentType !== "DELIVERED" ? "cb-pulse-dot" : ""}`} />
                    Status
                  </div>
                  <h1
                    className="cb-display mt-4 text-4xl text-slate-900 md:text-6xl"
                    data-testid="tracking-current-status"
                  >
                    {currentStep?.typeLabel || "—"}
                  </h1>
                  {currentEvent?.nameOfSigner && currentType === "DELIVERED" ? (
                    <p className="mt-3 text-sm text-slate-600">
                      Empfangen von ·{" "}
                      <span className="font-semibold text-slate-900">
                        {currentEvent.nameOfSigner}
                      </span>
                    </p>
                  ) : null}
                  {proofUrl && currentType === "DELIVERED" ? (
                    <a
                      href={proofUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-5 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 transition-colors hover:border-indigo-300 hover:text-indigo-600"
                      data-testid="tracking-proof-link"
                    >
                      <FileText size={16} />
                      Zustellnachweis ansehen
                      <ExternalLink size={12} className="opacity-60" />
                    </a>
                  ) : null}
                </div>
                <div className="md:col-span-5">
                  <div className="text-xs font-medium uppercase tracking-wider text-slate-500">
                    Eckdaten
                  </div>
                  <div className="mt-3">
                    <DataPanel
                      items={[
                        { label: "Abholung geplant", value: collection },
                        { label: "Zustellung geplant", value: delivery },
                        { label: "Abgeholt am", value: order.pickedUpDate ? formatDateTime(order.pickedUpDate) : null },
                        { label: "Zugestellt am", value: order.deliveredDate ? formatDateTime(order.deliveredDate) : null },
                        { label: "Versandart", value: TRANSPORT_LABELS[order.transportType] || order.transportType },
                        { label: "Sendungsnummer", value: reference },
                      ]}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* TIMELINE CARD */}
            <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200 md:p-8">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-xs font-medium uppercase tracking-wider text-slate-500">
                  Verlauf
                </div>
                <div className="text-xs font-medium text-slate-500">
                  {(data.steps || []).filter((s) => s.status === "SUCCESS").length} von {data.steps?.length || 0} Schritten
                </div>
              </div>
              <StepperTimeline steps={data.steps || []} />
            </section>

            {/* MAP CARD */}
            {order?.map?.marker?.length ? (
              <section className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200">
                <TrackingMap markers={order.map.marker} />
              </section>
            ) : null}

            {/* SHARE LINK */}
            <ShareLink reference={reference} plz={plz} />
          </div>
        )}
      </main>
    </div>
  );
};

const LoadingState = () => (
  <div
    className="flex flex-col items-center justify-center gap-3 rounded-2xl bg-white py-24 text-slate-500 shadow-sm ring-1 ring-slate-200"
    data-testid="tracking-loading"
  >
    <Loader2 className="animate-spin text-indigo-600" size={36} />
    <div className="text-sm font-medium text-slate-700">
      Lade Tracking-Daten …
    </div>
    <div className="text-xs text-slate-400">
      Bitte einen Moment Geduld (Sicherheitsprüfung läuft)
    </div>
  </div>
);

const ErrorBlock = ({ message }) => (
  <div
    className="flex flex-col items-center justify-center rounded-2xl bg-white p-12 text-center shadow-sm ring-1 ring-slate-200 md:p-20"
    data-testid="tracking-error"
  >
    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-100 text-red-600">
      <AlertTriangle size={26} strokeWidth={2} />
    </div>
    <h2 className="cb-display mt-5 text-2xl text-slate-900 md:text-3xl">
      Ein Fehler ist aufgetreten
    </h2>
    <p className="mt-3 max-w-md text-sm text-slate-600">{message}</p>
    <Link
      to="/"
      className="mt-7 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700"
      data-testid="error-back-link"
    >
      <ArrowLeft size={14} /> Neue Sendung verfolgen
    </Link>
  </div>
);

const EmptyResult = ({ reference }) => (
  <div
    className="flex flex-col items-center justify-center rounded-2xl bg-white p-12 text-center shadow-sm ring-1 ring-slate-200 md:p-20"
    data-testid="tracking-empty"
  >
    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-600">
      <PackageSearch size={26} strokeWidth={2} />
    </div>
    <h2 className="cb-display mt-5 text-2xl text-slate-900 md:text-3xl">
      Keine Daten gefunden
    </h2>
    <p className="mt-3 max-w-md text-sm text-slate-600">
      Für die Sendungsnummer{" "}
      <span className="font-mono font-semibold text-slate-900">{reference}</span>{" "}
      konnten keine Tracking-Daten gefunden werden. Bitte Sendungsnummer und PLZ
      des Empfängers prüfen.
    </p>
    <Link
      to="/"
      className="mt-7 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700"
      data-testid="empty-back-link"
    >
      <ArrowLeft size={14} /> Andere Sendung verfolgen
    </Link>
  </div>
);
